import { useEffect, useCallback, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/safeClient';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushSubscription() {
  const { user } = useAuth();
  const subscribedRef = useRef(false);
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  // Fetch VAPID public key from server
  useEffect(() => {
    const fetchKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-vapid-key');
        if (!error && data?.publicKey) {
          setVapidKey(data.publicKey);
        }
      } catch (e) {
        console.error('[Push] Failed to fetch VAPID key:', e);
      }
    };
    fetchKey();
  }, []);

  const subscribe = useCallback(async () => {
    if (!user || !vapidKey || subscribedRef.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const reg = registration as ServiceWorkerRegistration & { pushManager: PushManager };
      
      // Check if already subscribed
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        await saveSubscription(existing, user.id);
        subscribedRef.current = true;
        return;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      // Subscribe
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
      });

      await saveSubscription(subscription, user.id);
      subscribedRef.current = true;
      console.log('[Push] Subscribed successfully');
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
    }
  }, [user, vapidKey]);

  const saveSubscription = async (subscription: PushSubscription, userId: string) => {
    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    const { error } = await supabase
      .from('push_subscriptions')
      .upsert(
        {
          user_id: userId,
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,endpoint' }
      );

    if (error) {
      console.error('[Push] Failed to save subscription:', error);
    }
  };

  useEffect(() => {
    subscribe();
  }, [subscribe]);

  return { subscribe };
}
